import { useState } from 'react';
import {
  BookOutlined,
  CalendarOutlined,
  DeleteOutlined,
  EditOutlined,
  FileTextOutlined,
  FolderOpenOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  TeamOutlined,
} from '@ant-design/icons';

import { Bento, MenuThaoTac, PageHeader, StatTile } from '../../../components/shared';
import { Badge, Button, Empty, Input, Skeleton } from '../../../components/ui';
import { useDieuHuong } from '../../../hooks/useDuongDan';
import { useLoi } from '../../../hooks/useLoi';
import { useModulePermissions } from '../../../hooks/usePermission';
import { useT } from '../../../i18n';
import { KHONG_CO, ngayVN, soVN } from '../../../lib/dinh-dang';
import { HopXacNhanThaoTac } from '../../nguoi-dung/components/HopXacNhanThaoTac';
import { FormKho } from '../components/FormKho';
import { BADGE_CHUA_NOI_INDEX, LOAI_TEP, QUYEN, THU_TU_LOAI_TEP } from '../constants';
import { CayChonPhongBan } from '../../co-cau-to-chuc/components/CayChonPhongBan';
import { useDanhSachKho, usePhongBan, useThongKe, useXoaKho } from '../hooks/useTriThuc';
import type { KnowledgeBase } from '../types';

type HopMo = { loai: 'tao' } | { loai: 'sua'; kho: KnowledgeBase } | { loai: 'xoa'; kho: KnowledgeBase } | null;

/**
 * Quản lý tri thức (`/tri-thuc`, phân hệ `KNOWLEDGE_MGMT`): số liệu chung và danh sách bộ tri thức của tổ
 * chức. Bấm một bộ để vào cây thư mục và tệp của nó. Lọc theo tên/mã và phòng ban ở server, phân trang
 * cursor bằng nút "Tải thêm".
 */
export function KhoTriThucView() {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const dieuHuong = useDieuHuong();
  const quyen = useModulePermissions(QUYEN);

  const [nhap, setNhap] = useState('');
  const [timKiem, setTimKiem] = useState('');
  const [phongBan, setPhongBan] = useState<string | undefined>();
  const [hop, setHop] = useState<HopMo>(null);

  const q = useDanhSachKho(timKiem, phongBan);
  const thongKe = useThongKe();
  const dsPhongBan = usePhongBan();
  const xoa = useXoaKho();

  const tieuDe = t('nav.muc.quanLyTriThuc');
  const coLoc = Boolean(timKiem || phongBan);
  const loiTaiDau = q.error && q.dong.length === 0 && !q.isFetchNextPageError;
  const tk = thongKe.data;

  const lamMoi = () => {
    void q.refetch();
    void thongKe.refetch();
  };

  const so = (n: number | undefined) =>
    thongKe.isLoading ? <Skeleton className="h-6 w-12" /> : n === undefined ? KHONG_CO : soVN(n, locale);

  return (
    <div className="flex h-full flex-col bg-subtle">
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <PageHeader
          breadcrumb={[{ label: t('nav.nhom.triThucAi') }, { label: tieuDe }]}
          titleIcon={<BookOutlined className="text-primary-600" />}
          title={tieuDe}
          subtitle={t('triThuc.kho.moTa')}
          actions={
            <>
              <Button
                variant="ghost"
                size="md"
                title={t('chung.lamMoi')}
                aria-label={t('chung.lamMoi')}
                onClick={lamMoi}
              >
                <ReloadOutlined className={q.isFetching || thongKe.isFetching ? 'animate-spin' : ''} />
              </Button>
              {quyen.khoTao && (
                <Button variant="primary" size="md" icon={<PlusOutlined />} onClick={() => setHop({ loai: 'tao' })}>
                  {t('triThuc.kho.taoBo')}
                </Button>
              )}
            </>
          }
        />

        <Bento>
          <StatTile
            nhan={t('triThuc.thongKe.tongBo')}
            so={so(tk?.total_knowledge_bases)}
            sub={t('triThuc.thongKe.tongBoSub')}
            icon={<BookOutlined />}
            tone="teal"
          />
          <StatTile
            nhan={t('triThuc.thongKe.tongTep')}
            so={so(tk?.total_files)}
            sub={tk ? t('triThuc.thongKe.tongTepSub', { so: soVN(tk.pdf_files_count, locale) }) : undefined}
            icon={<FileTextOutlined />}
            tone="primary"
          />
          <StatTile
            nhan={t('triThuc.thongKe.phongBan')}
            so={so(tk?.departments_using_count)}
            sub={t('triThuc.thongKe.phongBanSub')}
            icon={<TeamOutlined />}
            tone="violet"
          />
          <StatTile
            nhan={t('triThuc.thongKe.capNhat')}
            so={thongKe.isLoading ? <Skeleton className="h-6 w-20" /> : tk?.last_updated_at ? ngayVN(tk.last_updated_at) : KHONG_CO}
            sub={t('triThuc.thongKe.capNhatSub')}
            icon={<CalendarOutlined />}
            tone="success"
          />
        </Bento>
        {thongKe.isError && (
          <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-default bg-surface px-4 py-3 shadow-sm">
            <span className="min-w-0 text-sm text-danger-700">
              {t('triThuc.thongKe.loi', { loi: dichLoi(thongKe.error) })}
            </span>
            <Button variant="outline" size="sm" onClick={() => void thongKe.refetch()}>
              {t('chung.thuLai')}
            </Button>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2.5 rounded-lg border border-default bg-surface px-4 py-3 shadow-sm">
          <Input
            allowClear
            className="max-w-xs"
            prefix={<SearchOutlined className="text-content-muted" />}
            placeholder={t('triThuc.kho.timKiem')}
            aria-label={t('triThuc.kho.timKiem')}
            value={nhap}
            onChange={(e) => {
              setNhap(e.target.value);
              if (!e.target.value) setTimKiem('');
            }}
            onPressEnter={() => setTimKiem(nhap.trim())}
            onBlur={() => setTimKiem(nhap.trim())}
          />
          <CayChonPhongBan
            className="min-w-[12rem]"
            ariaLabel={t('triThuc.kho.locPhongBan')}
            ds={dsPhongBan.data?.items ?? []}
            loading={dsPhongBan.isLoading}
            placeholder={t('triThuc.kho.tatCaPhongBan')}
            khongCo={t('nguoiDung.fPhongBanKhongCo')}
            value={phongBan ?? null}
            onChange={(v) => setPhongBan(v ?? undefined)}
          />
        </div>

        <section aria-label={t('triThuc.kho.danhSach')} className="space-y-4">
          {loiTaiDau ? (
            <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-default bg-surface px-4 py-3 shadow-sm">
              <span className="min-w-0 text-sm text-danger-700">{t('triThuc.kho.loiDanhSach', { loi: dichLoi(q.error) })}</span>
              <Button variant="outline" size="sm" onClick={() => void q.refetch()}>
                {t('chung.thuLai')}
              </Button>
            </div>
          ) : q.isLoading ? (
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" aria-busy="true">
              {Array.from({ length: 6 }, (_, i) => (
                <Skeleton key={i} className="h-48 rounded-lg" />
              ))}
            </div>
          ) : q.dong.length === 0 ? (
            <div className="rounded-lg border border-default bg-surface shadow-sm">
              <Empty
                icon={BookOutlined}
                // Rỗng vì bộ lọc: bỏ mô tả và lời mời hành động.
                title={t(coLoc ? 'triThuc.kho.rongLoc' : 'triThuc.kho.rongThat')}
                description={coLoc ? undefined : t('triThuc.kho.rongThatMo')}
                action={
                  coLoc || !quyen.khoTao ? undefined : (
                    <Button variant="primary" size="md" icon={<PlusOutlined />} onClick={() => setHop({ loai: 'tao' })}>
                      {t('triThuc.kho.taoBoDauTien')}
                    </Button>
                  )
                }
              />
            </div>
          ) : (
            <>
              <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {q.dong.map((kho) => (
                  <li key={kho.id}>
                    <TheKho
                      kho={kho}
                      onMo={() => dieuHuong(`/tri-thuc/kho/${kho.id}`)}
                      onSua={quyen.khoSua ? () => setHop({ loai: 'sua', kho }) : undefined}
                      onXoa={quyen.khoXoa ? () => setHop({ loai: 'xoa', kho }) : undefined}
                    />
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-default bg-surface px-4 py-3 shadow-sm">
                <span className="text-xs text-content-muted">
                  {t(q.hasNextPage ? 'triThuc.kho.dangHienCoThem' : 'triThuc.kho.dangHien', { so: soVN(q.dong.length, locale) })}
                </span>
                {q.isFetchNextPageError && (
                  <span role="alert" className="min-w-0 flex-1 text-xs text-danger-700">
                    {t('triThuc.loiTaiThem', { loi: dichLoi(q.error) })}
                  </span>
                )}
                {q.hasNextPage && (
                  <Button variant="outline" size="sm" loading={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
                    {q.isFetchNextPageError ? t('chung.thuLai') : t('chung.taiThem')}
                  </Button>
                )}
              </div>
            </>
          )}
        </section>
      </div>

      {(hop?.loai === 'tao' || hop?.loai === 'sua') && (
        <FormKho kho={hop.loai === 'sua' ? hop.kho : undefined} open onClose={() => setHop(null)} />
      )}
      {hop?.loai === 'xoa' && (
        <HopXacNhanThaoTac
          open
          tieuDe={t('triThuc.kho.xoaTieuDe')}
          than={t('triThuc.kho.xoaThan', { ten: hop.kho.name, so: soVN(hop.kho.file_counts.total, locale) })}
          nhanXacNhan={t('triThuc.kho.xoaBo')}
          nguyHiem
          dangChay={xoa.isPending}
          loi={xoa.error ? dichLoi(xoa.error) : null}
          onClose={() => {
            xoa.reset();
            setHop(null);
          }}
          onXacNhan={() =>
            xoa.mutate(hop.kho, {
              onSuccess: () => setHop(null),
            })
          }
        />
      )}
    </div>
  );
}

/** Thẻ một bộ tri thức. Tên là nút mở chi tiết; menu `⋮` gom sửa, xoá (ADR-UI-0005). */
function TheKho({
  kho,
  onMo,
  onSua,
  onXoa,
}: {
  kho: KnowledgeBase;
  onMo: () => void;
  onSua: (() => void) | undefined;
  onXoa: (() => void) | undefined;
}) {
  const { t, locale } = useT();
  const loai = THU_TU_LOAI_TEP.filter((l) => kho.file_counts[l.toLowerCase() as Lowercase<typeof l>] > 0);
  const duocDung = kho.permitted_departments.map((p) => p.name ?? KHONG_CO).join(', ');

  return (
    <article className="flex h-full flex-col gap-3 rounded-lg border border-default bg-surface p-4 shadow-sm transition-shadow hover:shadow-md">
      <div className="flex items-start gap-3">
        <span className="flex size-9 shrink-0 items-center justify-center rounded bg-teal-50 text-teal-700">
          <BookOutlined aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-semibold text-content-primary">
            <button
              type="button"
              onClick={onMo}
              className="line-clamp-2 text-left transition-colors hover:text-primary-700 focus-visible:text-primary-700"
            >
              {kho.name}
            </button>
          </h2>
          <span className="block truncate font-mono text-2xs text-content-muted">{kho.code}</span>
        </div>
        <MenuThaoTac
          tenBanGhi={kho.name}
          muc={[
            { khoa: 'mo', nhan: t('triThuc.kho.moBo'), icon: <FolderOpenOutlined />, nhom: 'xem', onChon: onMo },
            { khoa: 'sua', nhan: t('triThuc.kho.suaBo'), icon: <EditOutlined />, hien: Boolean(onSua), onChon: () => onSua?.() },
            {
              khoa: 'xoa',
              nhan: t('triThuc.kho.xoaBo'),
              icon: <DeleteOutlined />,
              nhom: 'pha-huy',
              hien: Boolean(onXoa),
              onChon: () => onXoa?.(),
            },
          ]}
        />
      </div>

      <p className="line-clamp-2 text-xs text-content-secondary">{kho.description || t('triThuc.kho.chuaCoMoTa')}</p>

      <dl className="space-y-1 text-xs">
        <div className="flex min-w-0 gap-1.5">
          <dt className="shrink-0 text-content-muted">{t('triThuc.kho.quanLy')}</dt>
          <dd className="min-w-0 truncate text-content-secondary">{kho.managing_department.name ?? KHONG_CO}</dd>
        </div>
        <div className="flex min-w-0 gap-1.5">
          <dt className="shrink-0 text-content-muted">{t('triThuc.kho.duocDung')}</dt>
          <dd className="min-w-0 truncate text-content-secondary" title={duocDung || undefined}>
            {kho.permitted_departments.length === 0 ? (
              <span className="text-warning-700">{t('triThuc.kho.chuaPhongBanNao')}</span>
            ) : (
              duocDung
            )}
          </dd>
        </div>
      </dl>

      {kho.collection_error && (
        <span title={kho.collection_error} className="self-start">
          <Badge variant={BADGE_CHUA_NOI_INDEX.variant}>{t(BADGE_CHUA_NOI_INDEX.khoa)}</Badge>
        </span>
      )}

      <div className="mt-auto space-y-1 border-t border-default pt-3 text-xs text-content-muted">
        {loai.length > 0 && (
          <p className="truncate">
            {loai
              .map((l) => t('triThuc.kho.loaiVaSo', {
                loai: t(LOAI_TEP[l]),
                so: soVN(kho.file_counts[l.toLowerCase() as Lowercase<typeof l>], locale),
              }))
              .join(' · ')}
          </p>
        )}
        <p className="flex flex-wrap gap-x-3">
          <span>{t('triThuc.kho.soTep', { so: soVN(kho.file_counts.total, locale) })}</span>
          <span>{t('triThuc.kho.capNhatNgay', { ngay: ngayVN(kho.updated_at) })}</span>
        </p>
      </div>
    </article>
  );
}
